import { type ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import countries from 'i18n-iso-countries'
import { runInAction } from 'mobx'
import classNames from 'classnames'

import { CountryCode, isStateFieldRequired } from '@campai/lib-common/utils/address'
import { getCountryName } from '@campai/lib-common/i18n'
import { getMinMaxLength } from '@campai/lib-common/utils/zod'
import { Address, AddressSchema } from '@campai/lib-common/entities'

import { ValidationErrors } from '@/utils/misc'
import {
    Button,
    Field,
    FieldError,
    FieldGroup,
    FieldLabel,
    Input,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
} from '@/components/new/ui'

type AddressFieldsChangeEventHandler = (value: Address) => void

//
// availableRecipient - Details can be set and placeholders shows info for a recipient
// availablePlace - Details can set and placeholder shows info for a place
// disabled - Details are shown only if set but can not be modified
// notAvailable - Details are not available even if set
//
type DetailsMode = 'availableRecipient' | 'availablePlace' | 'disabled' | 'notAvailable'

export type AddressFieldsBaseProps = {
    showLabels?: boolean
    details1: DetailsMode
    details2: DetailsMode
    countryDisabled?: boolean
    countryHidden?: boolean
    restrictCountries?: CountryCode[]
    preferredCountry?: CountryCode
}

type AddressFieldsContainerProps = {
    label?: string
    info?: ReactNode
    required?: boolean
    disabled?: boolean
    readOnly?: boolean
    className?: string
}

type AddressFieldsProps = AddressFieldsContainerProps &
    AddressFieldsBaseProps & {
        name: string
        errors?: ValidationErrors
        autoComplete?: boolean
        autoCompleteOff?: boolean
        value: Address
        onChange: AddressFieldsChangeEventHandler
        addressLineInputId: string
        state?: boolean
        states: Array<{ value: string; label: string }>
    }

export const AddressFields = (props: AddressFieldsProps) => {
    const {
        className,
        addressLineInputId,
        errors,
        value,
        onChange,
        showLabels,
        details1,
        details2,
        state,
        states,
        countryDisabled,
        countryHidden,
        restrictCountries,
        preferredCountry,
        ...otherContainerProperties
    } = props

    const { label, name, info, required, disabled, readOnly, autoCompleteOff } = otherContainerProperties

    const { t, i18n } = useTranslation()

    const i18nKey = 'components.addressForm.addressFields'
    const zipMaxLength = 10
    const fieldIdPrefix = useMemo(() => `address-form-${name.replace(/[.[\]]/g, '-')}`, [name])
    const zipId = `${fieldIdPrefix}-zip`
    const cityId = `${fieldIdPrefix}-city`
    const stateId = `${fieldIdPrefix}-state`
    const countryId = `${fieldIdPrefix}-country`

    const countryCodes = useMemo(() => {
        let result: string[] = restrictCountries?.length ? restrictCountries : Object.keys(countries.getAlpha2Codes())

        if (preferredCountry) {
            result = result.filter((country) => country !== preferredCountry)
            // push in front with a divider
            result = [preferredCountry, '--', ...result]
        }

        return result
    }, [preferredCountry, restrictCountries])
    const selectedStateLabel = useMemo(
        () => states.find((stateOption) => stateOption.value === value.state)?.label,
        [states, value.state]
    )

    const { detailsInfo1, detailsInfo2 } = useMemo(() => {
        const detailsInfo1: { show: boolean; isAvailable: boolean; disabled: boolean; placeholder: string } = {
            show: false,
            isAvailable: false,
            disabled: false,
            placeholder: '',
        }
        const detailsInfo2: { show: boolean; isAvailable: boolean; disabled: boolean; placeholder: string } = {
            show: false,
            isAvailable: false,
            disabled: false,
            placeholder: '',
        }

        switch (details1) {
            case 'availableRecipient':
                detailsInfo1.show = value.details1 !== undefined
                detailsInfo1.isAvailable = true
                detailsInfo1.placeholder = t(`${i18nKey}.detailsRecipient1`)
                break
            case 'availablePlace':
                detailsInfo1.show = value.details1 !== undefined
                detailsInfo1.isAvailable = true
                detailsInfo1.placeholder = t(`${i18nKey}.detailsRecipient2`)
                break
            case 'disabled':
                detailsInfo1.show = value.details1 !== undefined
                detailsInfo1.disabled = true
                break
            case 'notAvailable':
                break
        }

        switch (details2) {
            case 'availableRecipient':
                detailsInfo2.show = value.details2 !== undefined
                detailsInfo2.isAvailable = true
                detailsInfo2.placeholder = t(`${i18nKey}.detailsPlace1`)
                break
            case 'availablePlace':
                detailsInfo2.show = value.details2 !== undefined
                detailsInfo2.isAvailable = true
                detailsInfo2.placeholder = t(`${i18nKey}.detailsPlace2`)
                break
            case 'disabled':
                detailsInfo2.show = value.details2 !== undefined
                detailsInfo2.disabled = true
                break
            case 'notAvailable':
                break
        }

        return { detailsInfo1, detailsInfo2 }
    }, [details1, details2, t, value.details1, value.details2])

    const handleAddDetails = useCallback(() => {
        runInAction(() => {
            onChange({
                ...value,
                details1: detailsInfo1.isAvailable ? '' : undefined,
                details2: detailsInfo2.isAvailable ? '' : undefined,
            })
        })
    }, [detailsInfo1.isAvailable, detailsInfo2.isAvailable, onChange, value])

    const handleRemoveDetails = useCallback(() => {
        runInAction(() => {
            onChange({
                ...value,
                details1: detailsInfo1.isAvailable ? undefined : value.details1,
                details2: detailsInfo2.isAvailable ? undefined : value.details2,
            })
        })
    }, [detailsInfo1.isAvailable, detailsInfo2.isAvailable, onChange, value])

    const showAddDetails =
        !detailsInfo1.show && !detailsInfo2.show && (detailsInfo1.isAvailable || detailsInfo2.isAvailable)
    const showRemoveDetails =
        (detailsInfo1.show || detailsInfo2.show) && !detailsInfo1.disabled && !detailsInfo2.disabled
    const showDetails1 = (detailsInfo1.isAvailable || detailsInfo1.disabled) && (detailsInfo1.show || detailsInfo2.show)
    const showDetails2 = (detailsInfo2.isAvailable || detailsInfo2.disabled) && (detailsInfo1.show || detailsInfo2.show)

    return (
        <div className={className}>
            {label && (
                <Field>
                    <FieldLabel required={required} hint={info}>
                        {label}
                    </FieldLabel>
                </Field>
            )}
            <FieldGroup className={classNames({ 'gap-y-3': !showLabels, 'gap-y-5': showLabels })}>
                {showAddDetails && (
                    <div>
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="text-link-secondary h-auto px-0 py-0 text-xs font-normal"
                            onClick={handleAddDetails}
                            disabled={disabled || readOnly}
                        >
                            {t(`${i18nKey}.addDetails`)}
                        </Button>
                    </div>
                )}
                {showRemoveDetails && (
                    <div>
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="text-link-secondary h-auto px-0 py-0 text-xs font-normal"
                            onClick={handleRemoveDetails}
                            disabled={disabled || readOnly}
                        >
                            {t(`${i18nKey}.removeDetails`)}
                        </Button>
                    </div>
                )}
                {showDetails1 && (
                    <Field>
                        {showLabels && <FieldLabel>{detailsInfo1.placeholder}</FieldLabel>}
                        <Input
                            {...getMinMaxLength(AddressSchema.shape.details1.unwrap())}
                            aria-invalid={!!errors?.getError(name, 'details1')}
                            className="shrink-0"
                            placeholder={!showLabels ? detailsInfo1.placeholder : undefined}
                            aria-label={!showLabels ? detailsInfo1.placeholder : undefined}
                            disabled={disabled || detailsInfo1.disabled}
                            readOnly={readOnly}
                            value={value.details1 || ''}
                            onChange={(event) => {
                                onChange({
                                    ...value,
                                    details1: event.target.value || '',
                                })
                            }}
                        />
                        <FieldError>{errors?.getError(name, 'details1')}</FieldError>
                    </Field>
                )}
                {showDetails2 && (
                    <Field>
                        {showLabels && <FieldLabel>{detailsInfo2.placeholder}</FieldLabel>}
                        <Input
                            {...getMinMaxLength(AddressSchema.shape.details2.unwrap())}
                            aria-invalid={!!errors?.getError(name, 'details2')}
                            className="shrink-0"
                            placeholder={!showLabels ? detailsInfo2.placeholder : undefined}
                            aria-label={!showLabels ? detailsInfo2.placeholder : undefined}
                            disabled={disabled || detailsInfo2.disabled}
                            readOnly={readOnly}
                            value={value.details2 || ''}
                            onChange={(event) => {
                                onChange({
                                    ...value,
                                    details2: event.target.value || '',
                                })
                            }}
                        />
                        <FieldError>{errors?.getError(name, 'details2')}</FieldError>
                    </Field>
                )}
                <Field>
                    {showLabels && (
                        <FieldLabel htmlFor={addressLineInputId} required={required} hint={info}>
                            {t(`${i18nKey}.addressLine`)}
                        </FieldLabel>
                    )}
                    <Input
                        {...getMinMaxLength(AddressSchema.shape.addressLine)}
                        autoComplete={autoCompleteOff ? 'off' : 'street-address'}
                        required={required}
                        id={addressLineInputId}
                        // added addressLine1 too because lib address validation uses that name
                        aria-invalid={
                            !!errors?.getError(name, 'addressLine') || !!errors?.getError(name, 'addressLine1')
                        }
                        className="shrink-0"
                        placeholder={showLabels ? '' : t(`${i18nKey}.addressLine`)}
                        aria-label={!showLabels ? t(`${i18nKey}.addressLine`) : undefined}
                        disabled={disabled}
                        readOnly={readOnly}
                        value={value.addressLine}
                        onChange={(event) => {
                            onChange({
                                ...value,
                                addressLine: event.target.value,
                            })
                        }}
                    />
                    <FieldError>
                        {errors?.getError(name, 'addressLine') || errors?.getError(name, 'addressLine1')}
                    </FieldError>
                </Field>
                <div className="flex shrink-0 gap-x-2">
                    <Field className="min-w-26 w-auto">
                        {showLabels && (
                            <FieldLabel htmlFor={zipId} required={required}>
                                {t(`${i18nKey}.zip`)}
                            </FieldLabel>
                        )}
                        <Input
                            autoComplete={autoCompleteOff ? 'off' : 'postal-code'}
                            required={required}
                            aria-invalid={!!errors?.getError(name, 'zip')}
                            className="shrink-0"
                            maxLength={zipMaxLength}
                            size={zipMaxLength}
                            id={zipId}
                            placeholder={showLabels ? '' : t(`${i18nKey}.zip`)}
                            aria-label={!showLabels ? t(`${i18nKey}.zip`) : undefined}
                            disabled={disabled}
                            readOnly={readOnly}
                            value={value.zip}
                            onChange={(event) => {
                                onChange({
                                    ...value,
                                    zip: event.target.value,
                                })
                            }}
                        />
                        <FieldError>{errors?.getError(name, 'zip')}</FieldError>
                    </Field>
                    <Field className="flex-1">
                        {showLabels && (
                            <FieldLabel htmlFor={cityId} required={required}>
                                {t(`${i18nKey}.city`)}
                            </FieldLabel>
                        )}
                        <Input
                            autoComplete={autoCompleteOff ? 'off' : 'address-level2'}
                            required={required}
                            aria-invalid={!!errors?.getError(name, 'city')}
                            className="flex-1"
                            placeholder={showLabels ? '' : t(`${i18nKey}.city`)}
                            aria-label={!showLabels ? t(`${i18nKey}.city`) : undefined}
                            id={cityId}
                            disabled={disabled}
                            readOnly={readOnly}
                            value={value.city}
                            onChange={(event) => {
                                onChange({
                                    ...value,
                                    city: event.target.value,
                                })
                            }}
                        />
                        <FieldError>{errors?.getError(name, 'city')}</FieldError>
                    </Field>
                    {state && states.length > 0 && (
                        <Field className="min-w-34 w-auto">
                            {showLabels && (
                                <FieldLabel htmlFor={stateId} required={required}>
                                    {t(`${i18nKey}.state`)}
                                </FieldLabel>
                            )}
                            <Select
                                value={value.state ?? ''}
                                onValueChange={(newValue) => {
                                    onChange({
                                        ...value,
                                        state: newValue ?? undefined,
                                    })
                                }}
                                disabled={disabled || readOnly}
                            >
                                <SelectTrigger
                                    id={stateId}
                                    className="w-full"
                                    aria-invalid={!!errors?.getError(name, 'state')}
                                    aria-label={!showLabels ? t(`${i18nKey}.state`) : undefined}
                                >
                                    <SelectValue>{selectedStateLabel || t(`${i18nKey}.state`)}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {states.map((stateOption) => (
                                            <SelectItem key={stateOption.value} value={stateOption.value}>
                                                {stateOption.label}
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                            <FieldError>{errors?.getError(name, 'state')}</FieldError>
                        </Field>
                    )}
                </div>
                {!countryHidden && (
                    <Field>
                        {showLabels && (
                            <FieldLabel htmlFor={countryId} required={required}>
                                {t(`${i18nKey}.country`)}
                            </FieldLabel>
                        )}
                        <Select
                            value={value.country}
                            onValueChange={(newValue) => {
                                onChange({
                                    ...value,
                                    country: newValue as CountryCode,
                                    state: isStateFieldRequired(newValue as CountryCode) ? value.state : '',
                                })
                            }}
                            disabled={disabled || readOnly || countryDisabled}
                        >
                            <SelectTrigger
                                id={countryId}
                                className="w-full"
                                aria-invalid={!!errors?.getError(name, 'country')}
                                aria-label={!showLabels ? t(`${i18nKey}.country`) : undefined}
                            >
                                <SelectValue>
                                    {value.country ? getCountryName(value.country, i18n.language) : ''}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    {countryCodes.map((country) =>
                                        country === '--' ? (
                                            <SelectSeparator key="divider" />
                                        ) : (
                                            <SelectItem key={country} value={country}>
                                                {getCountryName(country, i18n.language)}
                                            </SelectItem>
                                        )
                                    )}
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                        <FieldError>{errors?.getError(name, 'country')}</FieldError>
                    </Field>
                )}
            </FieldGroup>
        </div>
    )
}
